import { useAuthStore } from "~/features/authentication/store";
import { useGoogleAuthStore } from "~/features/authentication/store/google-auth";
import type { ILoginResponse } from "~/features/authentication/types";
import { useUserStore } from "~/store/global/user.store";

declare global {
    interface Window {
        google: any;
    }
}

export default function useOneTapHandler() {
    const { $serverPosthog } = useNuxtApp();

    const user = ref(null);
    const clientId = useRuntimeConfig().public.googleClientId;
    const { googleOneTapAuth } = useGoogleAuthStore();
    const { getUtm } = useUtm();
    const authStore = useAuthStore();

    const { socialAuthCompleted, authIsInprogress } = storeToRefs(authStore);

    const initializeGoogleOneTap = () => {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.setAttribute("data-use_fedcm_for_prompt", "true");
        script.setAttribute("data-cancel_on_tap_outside", "false");
        script.onload = () => {
            window.google?.accounts.id.initialize({
                client_id: clientId,
                callback: handleCredentialResponse,
                cancel_on_tap_outside: false,
            });

            window.google?.accounts.id.prompt(); // Might not use this with FedCM explicitly
        };
        document.body.appendChild(script);
    };
    const { userData } = storeToRefs(useUserStore());

    const handleGtmEvent = (isNewCustomer: boolean) => {
        $serverPosthog?.capture("aihd_user_authenticated", {
            $set: { name: userData.value.email, email: userData.value.email },
            $set_once: { initial_url: useRoute().path },
        });
        if (isNewCustomer) {
            // @ts-ignore
            window.dataLayer.push({
                event: "Social Signup",
            });

            $serverPosthog?.capture("aihd_user_signed_up");

            return;
        }

        // @ts-ignore
        window.dataLayer?.push({
            event: "Social Login",
        });
    };
    const runtimeConfig = useRuntimeConfig();
    const { openSnackbar } = useSnackbar();
    const cookies = useCookie(`ph_${runtimeConfig.public.posthogPublicKey}_posthog`);
    const handleCredentialResponse = async (response: any) => {
        authIsInprogress.value = true;
        const { data, error, status } = await googleOneTapAuth({
            ...getUtm({ credential: response.credential }),
            // user_distinct_id: cookies.value?.distinct_id,
            disable_marketing_emails: authStore.hasEmailPermission,
        });

        if (data.value) {
            const response = data.value as ILoginResponse;
            useCookie("access_token", {
                maxAge: response.expires_in,
            }).value = response.access_token;

            useCookie("refresh_token", {
                maxAge: response.refresh_expires_in,
            }).value = response.refresh_token;
            const currentDate = new Date();
            const futureTimestamp = currentDate.getTime() + response.expires_in * 1000;
            useCookie("token_expires_in", {
                maxAge: response.refresh_expires_in,
            }).value = futureTimestamp.toString();

            handleGtmEvent(response.is_new_customer as boolean);
            socialAuthCompleted.value = true;
            return;
        }
        if (error.value) {
            authIsInprogress.value = false;
            // openSnackbar({
            //     state: "error",
            //     description: "something went wrong, please try again",
            // });
        }
    };

    return {
        user,
        initializeGoogleOneTap,
    };
}
